import { useCallback, useEffect, useMemo, useState } from 'react'
import type { RepositoryState, Vehicle, VehicleRepository } from '../../data/vehicles'
import { DemoVehicleRepository } from '../../data/vehicles'
import { getLocalizedPath } from '../../i18n/routes'
import { useI18n } from '../../i18n/I18nProvider'
import { Button } from '../../ui/Button'
import { Link } from '../../ui/Link'
import { translateHome } from './messages'

const defaultRepository = new DemoVehicleRepository({ listLimit: 1 })

function VehicleImage({ vehicle }: { vehicle: Vehicle }) {
  const { language } = useI18n()
  const [failed, setFailed] = useState(false)
  const label = `${vehicle.make} ${vehicle.model}`
  const t = useCallback(
    (key: Parameters<typeof translateHome>[1], values?: Record<string, string | number>) =>
      translateHome(language, key, values),
    [language],
  )

  if (failed) {
    return (
      <div className="home-vehicle__image-fallback" role="img" aria-label={t('vehicleFallback')}>
        <span className="home-vehicle__fallback-mark" aria-hidden="true" />
        <span>{t('vehicleFallback')}</span>
      </div>
    )
  }

  return (
    <img
      className="home-vehicle__image"
      src={vehicle.images[0]?.src}
      alt={t('vehicleAlt', { vehicle: label })}
      width="567"
      height="425"
      loading="eager"
      fetchPriority="high"
      decoding="async"
      style={{ objectPosition: vehicle.images[0]?.focalPoint }}
      onError={() => setFailed(true)}
    />
  )
}

function VehicleCard({ vehicle }: { vehicle: Vehicle }) {
  const { language } = useI18n()
  const label = `${vehicle.make} ${vehicle.model}`
  const t = useCallback(
    (key: Parameters<typeof translateHome>[1], values?: Record<string, string | number>) =>
      translateHome(language, key, values),
    [language],
  )

  return (
    <article className="home-vehicle">
      <div className="home-vehicle__media">
        <VehicleImage vehicle={vehicle} />
      </div>
      <div className="home-vehicle__body">
        <p className="home-vehicle__make">{vehicle.make}</p>
        <h2>{vehicle.model}</h2>
        <Link
          variant="text"
          to={getLocalizedPath('vehicle', language, { slug: vehicle.slug })}
          aria-label={t('vehicleView', { vehicle: label })}
        >
          {t('vehicleView', { vehicle: label })}
        </Link>
      </div>
    </article>
  )
}

export function HomeVehicleSection({ repository = defaultRepository }: { repository?: VehicleRepository }) {
  const { language } = useI18n()
  const [state, setState] = useState<RepositoryState<readonly Vehicle[]>>({ status: 'loading' })
  const [request, setRequest] = useState(0)
  const t = useMemo(
    () => (key: Parameters<typeof translateHome>[1], values?: Record<string, string | number>) =>
      translateHome(language, key, values),
    [language],
  )

  useEffect(() => {
    let active = true
    setState({ status: 'loading' })
    repository
      .list()
      .then((result) => {
        if (active) setState(result)
      })
      .catch((error: unknown) => {
        if (active) setState({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) })
      })
    return () => {
      active = false
    }
  }, [repository, request])

  return (
    <section className="home-stock" aria-labelledby="home-vehicles-title">
      <div className="page-frame home-stock__inner">
        <div className="home-stock__heading">
          <h1 id="home-vehicles-title" tabIndex={-1}>{t('vehicleTitle')}</h1>
          <Link variant="secondary" to={getLocalizedPath('vehicles', language)}>
            {t('vehiclesAction')}
          </Link>
        </div>

        <div className="home-stock__content" aria-live="polite" aria-busy={state.status === 'loading'}>
          {state.status === 'loading' ? (
            <div className="home-stock__state" role="status">
              <span className="home-stock__loading-mark" aria-hidden="true" />
              <p>{t('vehicleLoading')}</p>
            </div>
          ) : null}

          {state.status === 'empty' ? (
            <div className="home-stock__state">
              <p>{t('vehicleEmpty')}</p>
              <Link variant="text" to={getLocalizedPath('vehicles', language)}>{t('vehiclesAction')}</Link>
            </div>
          ) : null}

          {state.status === 'error' ? (
            <div className="home-stock__state" role="alert">
              <p>{t('vehicleError')}</p>
              <Button variant="secondary" onClick={() => setRequest((value) => value + 1)}>
                {t('vehicleRetry')}
              </Button>
            </div>
          ) : null}

          {state.status === 'ready' ? (
            <div className="home-stock__grid">
              {state.data.slice(0, 3).map((vehicle) => <VehicleCard key={vehicle.id} vehicle={vehicle} />)}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
